import { mergePrinterLists } from '@incashflow/shared/receipt/printerList';
import { databaseConfig } from '../../config/database';
import type {
  CupsPrinterInfo,
  ReceiptPaperSize,
  ThermalReceiptData,
} from '../../printing/thermalReceiptTypes';
import {
  fetchAvailableReceiptPrinters,
  printReceiptViaApi,
} from './receiptPrinterApiClient';
import {
  listReceiptPrintersLocal,
  printBarcodeTestLocal,
  printThermalReceipt,
} from '../../printing/receiptPrintClient';

const API = databaseConfig.mongodb.apiBaseUrl.replace(/\/$/, '');

export type IdentifiedBranch = {
  visibleId: string;
  name: string;
  address: string;
  code: string;
  isActive: boolean;
};

export type IdentifyTerminalData = {
  user: { username: string; role: string; name?: string };
  token: { accessToken: string };
  storeName: string;
  branches: IdentifiedBranch[];
};

export async function identifyTerminal(
  username: string,
  password: string,
): Promise<{ ok: true; data: IdentifyTerminalData } | { ok: false; status: number }> {
  const res = await fetch(`${API}/api/auth/terminal-setup/identify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  const json = (await res.json().catch(() => ({}))) as { data?: IdentifyTerminalData };
  if (!res.ok || !json.data?.token?.accessToken) {
    return { ok: false, status: res.status };
  }
  return { ok: true, data: json.data };
}

export async function listSetupPrinters(token: string): Promise<CupsPrinterInfo[]> {
  const fromApi = await fetchAvailableReceiptPrinters(token).catch(
    () => [] as CupsPrinterInfo[],
  );
  const fromLocal = await listReceiptPrintersLocal();
  return mergePrinterLists(fromApi, fromLocal);
}

export async function printReceiptTest(
  token: string,
  printerName: string,
  data: ThermalReceiptData,
  paperSize?: ReceiptPaperSize,
): Promise<boolean> {
  // Electron: print locally first (Windows OS queues like Aclas are not on CUPS).
  const local = await printThermalReceipt(data, printerName, paperSize);
  if (local.ok) return true;
  const result = await printReceiptViaApi(token, data, printerName, paperSize);
  return result.ok;
}

export type BarcodeTestContentOptions = {
  includeProductName?: boolean;
  includePriceTtc?: boolean;
};

export async function printBarcodeTest(
  token: string,
  printerName: string,
  content?: BarcodeTestContentOptions,
): Promise<boolean> {
  // Electron: print locally first (Windows OS queues like Gainscha are not on CUPS/API host).
  const local = await printBarcodeTestLocal(printerName, content);
  if (local.ok) return true;
  const res = await fetch(`${API}/api/kiosk/barcode-printer/test`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      printerName,
      includeProductName: content?.includeProductName !== false,
      includePriceTtc: content?.includePriceTtc === true,
    }),
  });
  return res.ok;
}
